import {Component, Inject} from '@angular/core';
import {FactSessionId, FindingId} from '@webmate-web-core/shared/types/ids';
import {WorkbenchViewComponent} from '../../../shared/components/viewcontainers/workbench-view.component';
import {WORKBENCH_VIEW_CONFIG, WorkbenchViewConfig} from '../../../shared/types';
import {WorkbenchLayoutManager} from '../../../shared/services/workbench-layout-manager.service';
import {
    QualitySenseObservation,
    WorkbenchQualitySenseService,
    WorkbenchQualitySenseStatus
} from '../../../shared/services/workbench-quality-sense.service';
import {WorkbenchCommandRegistry} from '../../../shared/services/workbench-command-registry.service';
import {QUALITY_SENSE_PANEL_CONTENT_ID, QUALITY_SENSE_TOGGLE_COMMAND_ID} from '../quality-sense-ids';

@Component({
    templateUrl: './quality-sense-observations-view.component.html',
    styleUrls: ['./quality-sense-observations-view.component.less']
})
export class QualitySenseObservationsViewComponent extends WorkbenchViewComponent {
    readonly state$ = this.qualitySenseService.activeState$;
    readonly panelContentId = QUALITY_SENSE_PANEL_CONTENT_ID;
    private readonly expandedFindingIds = new Map<FactSessionId, FindingId>();

    constructor(layoutManager: WorkbenchLayoutManager,
                @Inject(WORKBENCH_VIEW_CONFIG) config: WorkbenchViewConfig<unknown>,
                private readonly qualitySenseService: WorkbenchQualitySenseService,
                private readonly commandRegistry: WorkbenchCommandRegistry) {
        super(layoutManager, config);
    }

    statusLabel(status: WorkbenchQualitySenseStatus): string {
        switch (status) {
            case 'idle': return 'Idle';
            case 'starting': return 'Starting';
            case 'monitoring': return 'Monitoring';
            case 'monitoring-disconnected': return 'Disconnected';
            case 'not-monitored': return 'Not monitored';
            case 'load-failed': return 'Load failed';
            case 'ended': return 'Session ended';
        }
    }

    observationType(observation: QualitySenseObservation): string {
        return observation.findingType === 'Network.HttpErrorResponse' ? 'HTTP' : 'CONSOLE';
    }

    observationTitle(observation: QualitySenseObservation): string {
        if (observation.title) {
            return observation.title;
        }
        return observation.findingType === 'Network.HttpErrorResponse'
            ? 'HTTP error response detected'
            : 'Console error detected';
    }

    showUnreadDividerAfter(observations: readonly QualitySenseObservation[], index: number): boolean {
        return index < observations.length - 1 &&
            observations[index].unread &&
            !observations.slice(index + 1).some(observation => observation.unread);
    }

    showObservations(status: WorkbenchQualitySenseStatus): boolean {
        return ['monitoring', 'monitoring-disconnected', 'load-failed', 'ended'].includes(status);
    }

    isObservationExpanded(factSessionId: FactSessionId | undefined, findingId: FindingId): boolean {
        return factSessionId !== undefined && this.expandedFindingIds.get(factSessionId) === findingId;
    }

    toggleObservation(factSessionId: FactSessionId | undefined, observation: QualitySenseObservation): void {
        if (!factSessionId) {
            return;
        }
        if (this.isObservationExpanded(factSessionId, observation.findingId)) {
            this.expandedFindingIds.delete(factSessionId);
            return;
        }

        this.expandedFindingIds.set(factSessionId, observation.findingId);
        this.qualitySenseService.markObservationSeen(observation.findingId);
        this.qualitySenseService.loadObservationDetails(observation.findingId);
    }

    retryObservation(observation: QualitySenseObservation): void {
        this.qualitySenseService.loadObservationDetails(observation.findingId);
    }

    observationAccessibleLabel(observation: QualitySenseObservation): string {
        const unread = observation.unread ? 'New observation. ' : '';
        const evidence = observation.associatedArtifacts.length === 1
            ? '1 evidence artifact.'
            : `${observation.associatedArtifacts.length} evidence artifacts.`;
        return `${unread}${this.observationType(observation)}. ${observation.severity}. ` +
            `${this.observationTitle(observation)}. ${evidence}`;
    }

    httpCondition(observation: QualitySenseObservation): string {
        const minimumStatus = observation.details?.predicate?.['minimumStatus'];
        return typeof minimumStatus === 'number'
            ? `Status code is at least ${minimumStatus}`
            : 'HTTP error response';
    }

    consoleCondition(observation: QualitySenseObservation): string {
        const levels = observation.details?.predicate?.['levels'];
        const messageRegex = observation.details?.predicate?.['messageRegex'];
        const levelText = Array.isArray(levels) && levels.every(level => typeof level === 'string')
            ? `Level: ${levels.join(', ')}`
            : 'Console message';
        return typeof messageRegex === 'string'
            ? `${levelText}; message matches /${messageRegex}/`
            : levelText;
    }

    consoleLocation(observation: QualitySenseObservation): string | undefined {
        const message = observation.details?.message;
        if (!message?.url) {
            return undefined;
        }
        if (message.line === undefined) {
            return message.url;
        }
        return `${message.url}:${message.line}${message.column === undefined ? '' : `:${message.column}`}`;
    }

    markAllSeen(): void {
        this.qualitySenseService.markAllSeen();
    }

    retry(): void {
        this.qualitySenseService.retryLoad();
    }

    close(): void {
        this.commandRegistry.executeCommand(QUALITY_SENSE_TOGGLE_COMMAND_ID);
    }
}
